import { useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronDown, Edit3, History, Link2, Loader2, RefreshCw } from 'lucide-react';
import {
  apiService,
  CronogramaListResponse,
  CronogramaRevisaoResponse,
  CronogramaTaskResponse,
} from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { cronogramaRepository } from '@/storage/cronogramaRepository';

type FormatarData = (valor: Date | string | number | null | undefined, opcoes?: Intl.DateTimeFormatOptions) => string;

/** Helper fora do componente: o `formatarData` vem do `useI18n()` de quem chama. */
function getMonthOptions(
  formatarData: FormatarData,
  periodoInicioIso?: string | null
): Array<{ label: string; value: string }> {
  const options: Array<{ label: string; value: string }> = [];
  const current = new Date();
  const fallbackStart = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() - 6, 1));
  const fallbackEnd = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 12, 1));

  let start = fallbackStart;
  if (periodoInicioIso) {
    const parsed = new Date(periodoInicioIso);
    if (!Number.isNaN(parsed.getTime())) {
      const inicioCronograma = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1));
      if (inicioCronograma > start) {
        start = inicioCronograma;
      }
    }
  }

  const end = start > fallbackEnd ? start : fallbackEnd;
  const startMonthIndex = start.getUTCFullYear() * 12 + start.getUTCMonth();
  const endMonthIndex = end.getUTCFullYear() * 12 + end.getUTCMonth();

  for (let i = startMonthIndex; i <= endMonthIndex; i++) {
    const year = Math.floor(i / 12);
    const month = i % 12;
    const d = new Date(Date.UTC(year, month, 1));
    const ano = d.getUTCFullYear();
    const mes = d.getUTCMonth() + 1;
    options.push({
      label: formatarData(d, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      value: `${ano}-${String(mes).padStart(2, '0')}`,
    });
  }

  return options;
}

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromDateInput(value: string): string | null {
  if (!value.trim()) return null;
  return `${value.trim()}T00:00:00.000Z`;
}

function statusBadgeClasses(status: CronogramaTaskResponse['statusExecucao']): string {
  switch (status) {
    case 'concluida':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'atrasada':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'pendente_vinculo':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'em_andamento':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

type EditFormState = {
  nomeTarefa: string;
  sismanaAntigo: string;
  area: string;
  local: string;
  semana: string;
  mes: string;
  inicioPlanejado: string;
  terminoPlanejado: string;
  duracaoDias: string;
  percentualOverride: string;
  ativoId: string;
};

function createEditState(task: CronogramaTaskResponse): EditFormState {
  return {
    nomeTarefa: task.nomeTarefa || '',
    sismanaAntigo: task.sismanaAntigo || '',
    area: task.area || '',
    local: task.local || '',
    semana: task.semana || '',
    mes: task.mes || '',
    inicioPlanejado: toDateInput(task.inicioPlanejado),
    terminoPlanejado: toDateInput(task.terminoPlanejado),
    duracaoDias: task.duracaoDias != null ? String(task.duracaoDias) : '',
    percentualOverride: task.percentualOverride != null ? String(task.percentualOverride) : '',
    ativoId: task.ativoId || '',
  };
}

function summarizeTasks(tarefas: CronogramaTaskResponse[]) {
  return {
    total: tarefas.length,
    concluidas: tarefas.filter((t) => t.statusExecucao === 'concluida').length,
    atrasadas: tarefas.filter((t) => t.statusExecucao === 'atrasada').length,
    pendentesVinculo: tarefas.filter((t) => t.statusExecucao === 'pendente_vinculo').length,
    emAndamento: tarefas.filter((t) => t.statusExecucao === 'em_andamento').length,
    pendentes: tarefas.filter((t) => t.statusExecucao === 'pendente').length,
  };
}

function TarefasTabela({
  tarefas,
  onEditar,
}: {
  tarefas: CronogramaTaskResponse[];
  onEditar: (task: CronogramaTaskResponse) => void;
}) {
  const { t, formatarData, formatarNumero } = useI18n();

  const percentual = (valor: number) =>
    t('cronograma.formato.percentual', {
      valor: formatarNumero(valor, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    });

  return (
    <table className="w-full text-xs border-collapse border border-rg-line min-w-[1000px]">
      <thead>
        <tr className="bg-schmersal-blue text-white">
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('cronograma.tabela.colunaTarefa')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.area')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.semana')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('cronograma.tabela.colunaInicio')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('cronograma.tabela.colunaTermino')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('cronograma.tabela.colunaVinculo')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.status')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-right">{t('cronograma.tabela.colunaPercentual')}</th>
          <th className="border border-schmersal-blue-light px-2 py-2 text-left">{t('comum.rotulo.acoes')}</th>
        </tr>
      </thead>
      <tbody>
        {tarefas.length === 0 && (
          <tr>
            <td colSpan={9} className="border border-schmersal-gray px-2 py-2 text-center text-schmersal-gray-medium">
              {t('cronograma.tabela.vazio')}
            </td>
          </tr>
        )}
        {tarefas.map((task, idx) => (
          <tr key={task.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-schmersal-gray-light/20'}>
            <td className="border border-schmersal-gray px-2 py-1.5">{task.nomeTarefa}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">{task.area || '-'}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">{task.semana || '-'}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">{formatarData(task.inicioPlanejado, { timeZone: 'UTC' })}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">{formatarData(task.terminoPlanejado, { timeZone: 'UTC' })}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">{t(`cronograma.statusVinculo.${task.statusVinculo}`)}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${statusBadgeClasses(task.statusExecucao)}`}>
                {t(`cronograma.statusExecucao.${task.statusExecucao}`)}
              </span>
            </td>
            <td className="border border-schmersal-gray px-2 py-1.5 text-right font-semibold">{percentual(task.percentualEfetivo)}</td>
            <td className="border border-schmersal-gray px-2 py-1.5">
              <button
                onClick={() => onEditar(task)}
                className="inline-flex items-center px-2 py-1 text-[11px] font-semibold rounded border border-schmersal-blue text-schmersal-blue hover:bg-schmersal-blue hover:text-white transition-colors"
              >
                <Edit3 className="h-3 w-3 mr-1" />
                {t('comum.acao.editar')}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CronogramaPage() {
  const { projeto } = useProjeto();
  const { t, formatarData, formatarNumero } = useI18n();

  const currentMonth = new Date();
  const [mesSelecionado, setMesSelecionado] = useState(
    `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, '0')}`
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [msgSincronizacao, setMsgSincronizacao] = useState<string | null>(null);

  const [cronograma, setCronograma] = useState<CronogramaListResponse | null>(null);
  const [revisoes, setRevisoes] = useState<CronogramaRevisaoResponse[]>([]);

  const [filtroSemana, setFiltroSemana] = useState('');
  const [filtroArea, setFiltroArea] = useState('');

  const [tarefaEdicao, setTarefaEdicao] = useState<CronogramaTaskResponse | null>(null);
  const [formEdicao, setFormEdicao] = useState<EditFormState | null>(null);

  const [ano, mes] = mesSelecionado.split('-').map(Number);

  const monthOptions = useMemo(
    () => getMonthOptions(formatarData, cronograma?.cronogramaProjeto?.periodoInicioIso),
    [formatarData, cronograma?.cronogramaProjeto?.periodoInicioIso]
  );

  useEffect(() => {
    if (!monthOptions.length) return;
    const possuiSelecionado = monthOptions.some((item) => item.value === mesSelecionado);
    if (!possuiSelecionado) {
      setMesSelecionado(monthOptions[0].value);
    }
  }, [monthOptions, mesSelecionado]);

  const semanasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const task of cronograma?.tarefas || []) {
      if (task.semana) set.add(task.semana);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cronograma?.tarefas]);

  const areasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const task of cronograma?.tarefas || []) {
      if (task.area) set.add(task.area);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cronograma?.tarefas]);

  const tarefasFiltradas = useMemo(() => {
    const source = cronograma?.tarefas || [];
    return source.filter((task) => {
      if (filtroSemana && task.semana !== filtroSemana) return false;
      if (filtroArea && task.area !== filtroArea) return false;
      return true;
    });
  }, [cronograma?.tarefas, filtroSemana, filtroArea]);

  const tarefasLevantamento = useMemo(
    () => tarefasFiltradas.filter((t) => t.tipoEtapa === 'levantamento'),
    [tarefasFiltradas]
  );

  const tarefasRevisao = useMemo(
    () => tarefasFiltradas.filter((t) => t.tipoEtapa === 'revisao'),
    [tarefasFiltradas]
  );

  const carregarDados = async () => {
    if (!projeto?.id) {
      setCronograma(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.listarCronograma({ projetoId: projeto.id, ano, mes });
      if (response.success && response.data) {
        setCronograma(response.data);
        // Salvar offline cache
        await cronogramaRepository.saveTasks(projeto.id, response.data.tarefas);
      } else {
        const cachedTasks = await cronogramaRepository.getTasks(projeto.id);
        if (cachedTasks && cachedTasks.length > 0) {
          setCronograma({
            possuiCronograma: true,
            cronogramaProjeto: null,
            resumo: summarizeTasks(cachedTasks),
            tarefas: cachedTasks,
          });
          setError(t('cronograma.mensagem.semConexao'));
        } else {
          setCronograma(null);
          setError(response.message || t('cronograma.mensagem.erroCarregar'));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar cronograma:', err);
      const cachedTasks = projeto?.id ? await cronogramaRepository.getTasks(projeto.id) : null;
      if (cachedTasks && cachedTasks.length > 0) {
        setCronograma({
          possuiCronograma: true,
          cronogramaProjeto: null,
          resumo: summarizeTasks(cachedTasks),
          tarefas: cachedTasks,
        });
        setError(t('cronograma.mensagem.semConexao'));
      } else {
        setCronograma(null);
        setError(err instanceof Error ? err.message : t('cronograma.mensagem.erroCarregarGenerico'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [projeto?.id, ano, mes]);

  const carregarRevisoes = async (tarefaId?: string) => {
    if (!projeto?.id) return;

    try {
      const response = await apiService.listarRevisoesCronograma({
        projetoId: projeto.id,
        tarefaId,
      });

      if (response.success && response.data) {
        setRevisoes(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar revisões:', err);
    }
  };

  const handleOpenEdit = (task: CronogramaTaskResponse) => {
    setTarefaEdicao(task);
    setFormEdicao(createEditState(task));
    carregarRevisoes(task.id);
  };

  const handleSaveEdit = async () => {
    if (!projeto?.id || !tarefaEdicao || !formEdicao || salvandoEdicao) return;

    setSalvandoEdicao(true);
    try {
      const payload = {
        nomeTarefa: formEdicao.nomeTarefa || null,
        sismanaAntigo: formEdicao.sismanaAntigo || null,
        area: formEdicao.area || null,
        local: formEdicao.local || null,
        semana: formEdicao.semana || null,
        mes: formEdicao.mes || null,
        inicioPlanejado: fromDateInput(formEdicao.inicioPlanejado),
        terminoPlanejado: fromDateInput(formEdicao.terminoPlanejado),
        duracaoDias: formEdicao.duracaoDias.trim() ? Number(formEdicao.duracaoDias) : null,
        percentualOverride: formEdicao.percentualOverride.trim() ? Number(formEdicao.percentualOverride) : null,
        ativoId: formEdicao.ativoId || null,
      };

      const response = await apiService.atualizarCronogramaTarefa({
        tarefaId: tarefaEdicao.id,
        projetoId: projeto.id,
        payload,
      });

      if (!response.success) {
        alert(response.message || t('cronograma.mensagem.falhaAtualizar'));
        return;
      }

      await Promise.all([
        carregarDados(),
        carregarRevisoes(tarefaEdicao.id),
      ]);
    } catch (err) {
      console.error('Erro ao salvar edição:', err);
      alert(err instanceof Error ? err.message : t('cronograma.mensagem.erroSalvar'));
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const handleSincronizar = async () => {
    if (!projeto?.id || sincronizando) return;
    setSincronizando(true);
    setMsgSincronizacao(null);
    try {
      const response = await apiService.sincronizarAtivosCronograma(projeto.id);
      if (response.success && response.data) {
        const { ativos_criados, tarefas_vinculadas } = response.data;
        if (ativos_criados === 0) {
          setMsgSincronizacao(t('cronograma.mensagem.nenhumaMaquinaPendente'));
        } else {
          setMsgSincronizacao(
            t('cronograma.mensagem.sincronizacaoConcluida', {
              ativos: formatarNumero(ativos_criados),
              tarefas: formatarNumero(tarefas_vinculadas),
            })
          );
        }
        await carregarDados();
      } else {
        setMsgSincronizacao(response.message || t('cronograma.mensagem.erroSincronizar'));
      }
    } catch (err) {
      setMsgSincronizacao(err instanceof Error ? err.message : t('cronograma.mensagem.erroSincronizarMaquinas'));
    } finally {
      setSincronizando(false);
    }
  };

  const temPendentes = (cronograma?.tarefas ?? []).some((t) => t.statusVinculo === 'pendente');

  return (
    <div className="space-y-6">
      <div className="bg-schmersal-blue rounded-lg shadow-sm p-6 text-white border border-schmersal-blue-light">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{t('cronograma.titulo')}</h1>
            <p className="mt-1 text-schmersal-gray-light text-sm md:text-base">
              {t('cronograma.subtitulo')}
            </p>
          </div>
          <div className="text-sm leading-relaxed bg-schmersal-blue-dark/40 rounded-lg px-4 py-3 border border-schmersal-blue-light/30">
            <p><strong>{t('cronograma.resumo.projeto')}</strong> {projeto?.nome || '-'}</p>
            <p><strong>{t('cronograma.resumo.totalTarefas')}</strong> {formatarNumero(cronograma?.resumo.total ?? 0)}</p>
            <p><strong>{t('cronograma.resumo.atualizacao')}</strong> {formatarData(new Date())}</p>
          </div>
        </div>
      </div>

      <div className="no-print flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3">
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
          <select
            value={mesSelecionado}
            onChange={(e) => setMesSelecionado(e.target.value)}
            className="pl-9 pr-10 py-2.5 w-72 border border-rg-line rounded-lg text-sm bg-white text-schmersal-gray-dark focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue appearance-none cursor-pointer shadow-sm"
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" />
        </div>

        <div className="flex gap-2">
          {temPendentes && (
            <button
              onClick={handleSincronizar}
              disabled={sincronizando || loading}
              className="inline-flex items-center justify-center px-4 py-2.5 border-2 border-amber-500 text-sm font-semibold rounded-lg bg-white text-amber-600 hover:bg-amber-500 hover:text-white disabled:opacity-50 transition-all shadow-md"
              title={t('cronograma.acao.sincronizarTitulo')}
            >
              {sincronizando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              {t('cronograma.acao.sincronizar')}
            </button>
          )}
          <button
            onClick={carregarDados}
            disabled={loading}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-schmersal-blue text-sm font-semibold rounded-lg bg-white text-schmersal-blue hover:bg-schmersal-blue hover:text-white disabled:opacity-50 transition-all shadow-md"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('comum.acao.atualizar')}
          </button>
        </div>
      </div>

      {msgSincronizacao && (
        <div className="bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm flex justify-between items-center">
          <span>{msgSincronizacao}</span>
          <button
            onClick={() => setMsgSincronizacao(null)}
            aria-label={t('comum.acao.fechar')}
            className="ml-4 text-amber-600 hover:text-amber-800 font-bold"
          >
            ✕
          </button>
        </div>
      )}

      {!projeto?.id && (
        <div className="bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
          {t('cronograma.estado.semProjeto')}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {loading && (
        <div className="bg-white border border-rg-line rounded-lg p-10 text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue" />
          <p className="mt-3 text-schmersal-gray-medium font-medium">{t('cronograma.estado.carregando')}</p>
        </div>
      )}

      {!loading && cronograma && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-rg-line">
          <div className="bg-schmersal-gray-dark px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h2 className="text-sm font-bold text-white uppercase">{t('cronograma.lista.titulo')}</h2>
            <div className="flex flex-wrap gap-2">
              <select
                value={filtroSemana}
                onChange={(e) => setFiltroSemana(e.target.value)}
                className="px-2 py-1 rounded border text-xs"
              >
                <option value="">{t('cronograma.filtro.todasSemanas')}</option>
                {semanasDisponiveis.map((semana) => (
                  <option key={semana} value={semana}>{semana}</option>
                ))}
              </select>
              <select
                value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}
                className="px-2 py-1 rounded border text-xs"
              >
                <option value="">{t('cronograma.filtro.todasAreas')}</option>
                {areasDisponiveis.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="p-4 bg-white space-y-6 overflow-x-auto">
            {!cronograma.possuiCronograma && (
              <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t('cronograma.lista.semCronograma')}
              </div>
            )}

            {/* Seção: Levantamentos */}
            <div>
              <h3 className="text-sm font-bold text-schmersal-blue uppercase mb-2 border-b-2 border-schmersal-blue pb-1">
                {t('cronograma.lista.levantamentos', { total: formatarNumero(tarefasLevantamento.length) })}
              </h3>
              <TarefasTabela tarefas={tarefasLevantamento} onEditar={handleOpenEdit} />
            </div>

            {/* Seção: Revisões */}
            <div>
              <h3 className="text-sm font-bold text-schmersal-gray-dark uppercase mb-2 border-b-2 border-schmersal-gray pb-1">
                {t('cronograma.lista.revisoes', { total: formatarNumero(tarefasRevisao.length) })}
              </h3>
              <TarefasTabela tarefas={tarefasRevisao} onEditar={handleOpenEdit} />
            </div>
          </div>
        </div>
      )}

      {tarefaEdicao && formEdicao && (
        <div className="rounded-lg border border-schmersal-blue-light bg-white shadow-sm">
          <div className="px-4 py-3 bg-schmersal-blue text-white flex items-center justify-between">
            <h3 className="text-sm font-bold uppercase">{t('cronograma.edicao.titulo')}</h3>
            <button
              onClick={() => {
                setTarefaEdicao(null);
                setFormEdicao(null);
              }}
              className="text-xs bg-white/20 px-2 py-1 rounded hover:bg-white/30"
            >
              {t('comum.acao.fechar')}
            </button>
          </div>

          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.nomeTarefa')}</label>
              <input
                value={formEdicao.nomeTarefa}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, nomeTarefa: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.sismanaAntigo')}</label>
              <input
                value={formEdicao.sismanaAntigo}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, sismanaAntigo: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('comum.rotulo.area')}</label>
              <input
                value={formEdicao.area}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, area: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('comum.rotulo.local')}</label>
              <input
                value={formEdicao.local}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, local: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('comum.rotulo.semana')}</label>
              <input
                value={formEdicao.semana}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, semana: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.mes')}</label>
              <input
                value={formEdicao.mes}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, mes: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.inicio')}</label>
              <input
                type="date"
                value={formEdicao.inicioPlanejado}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, inicioPlanejado: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.termino')}</label>
              <input
                type="date"
                value={formEdicao.terminoPlanejado}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, terminoPlanejado: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.duracaoDias')}</label>
              <input
                type="number"
                min={0}
                value={formEdicao.duracaoDias}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, duracaoDias: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.percentualOverride')}</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={formEdicao.percentualOverride}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, percentualOverride: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t('cronograma.edicao.ativoId')}</label>
              <input
                value={formEdicao.ativoId}
                onChange={(e) => setFormEdicao((prev) => (prev ? { ...prev, ativoId: e.target.value } : prev))}
                className="w-full border rounded px-2 py-1.5"
              />
            </div>
          </div>

          <div className="px-4 pb-4 flex flex-wrap gap-2">
            <button
              onClick={handleSaveEdit}
              disabled={salvandoEdicao}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded bg-schmersal-blue text-white hover:bg-schmersal-blue-dark disabled:opacity-50"
            >
              {salvandoEdicao ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}
              {t('cronograma.acao.salvarAjustes')}
            </button>
            <button
              onClick={() => carregarRevisoes(tarefaEdicao.id)}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded border border-schmersal-blue text-schmersal-blue hover:bg-schmersal-blue hover:text-white"
            >
              <History className="h-4 w-4 mr-2" />
              {t('cronograma.acao.atualizarRevisoes')}
            </button>
          </div>

          <div className="px-4 pb-4">
            <h4 className="text-sm font-bold text-schmersal-gray-dark mb-2">{t('cronograma.revisoes.titulo')}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse border border-schmersal-gray-light">
                <thead>
                  <tr className="bg-schmersal-gray-light/40">
                    <th className="border border-schmersal-gray-light px-2 py-2 text-left">{t('comum.rotulo.data')}</th>
                    <th className="border border-schmersal-gray-light px-2 py-2 text-left">{t('cronograma.revisoes.colunaUsuario')}</th>
                    <th className="border border-schmersal-gray-light px-2 py-2 text-left">{t('cronograma.revisoes.colunaCampo')}</th>
                    <th className="border border-schmersal-gray-light px-2 py-2 text-left">{t('cronograma.revisoes.colunaValorAnterior')}</th>
                    <th className="border border-schmersal-gray-light px-2 py-2 text-left">{t('cronograma.revisoes.colunaNovoValor')}</th>
                  </tr>
                </thead>
                <tbody>
                  {revisoes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="border border-schmersal-gray-light px-2 py-2 text-center text-schmersal-gray-medium">
                        {t('cronograma.revisoes.vazio')}
                      </td>
                    </tr>
                  )}
                  {revisoes.map((item) => (
                    <tr key={item.id}>
                      <td className="border border-schmersal-gray-light px-2 py-1.5">{formatarData(item.createdAt, { timeZone: 'UTC' })}</td>
                      <td className="border border-schmersal-gray-light px-2 py-1.5">{item.usuarioNome || t('cronograma.revisoes.sistema')}</td>
                      <td className="border border-schmersal-gray-light px-2 py-1.5">{item.campo}</td>
                      <td className="border border-schmersal-gray-light px-2 py-1.5">{JSON.stringify(item.valorAnterior)}</td>
                      <td className="border border-schmersal-gray-light px-2 py-1.5">{JSON.stringify(item.valorNovo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
